import {filterDOMProps as $9GmRZ$filterDOMProps, mergeProps as $9GmRZ$mergeProps, useId as $9GmRZ$useId, chain as $9GmRZ$chain} from "@react-aria/utils";
import $9GmRZ$react, {useMemo as $9GmRZ$useMemo} from "react";
import {ListKeyboardDelegate as $9GmRZ$ListKeyboardDelegate, useTypeSelect as $9GmRZ$useTypeSelect} from "@react-aria/selection";
import {setInteractionModality as $9GmRZ$setInteractionModality, useInteractionModality as $9GmRZ$useInteractionModality} from "@react-aria/interactions";
import {useCollator as $9GmRZ$useCollator} from "@react-aria/i18n";
import {useField as $9GmRZ$useField} from "@react-aria/label";
import {useMenuTrigger as $9GmRZ$useMenuTrigger} from "@react-aria/menu";
import {useVisuallyHidden as $9GmRZ$useVisuallyHidden} from "@react-aria/visually-hidden";

function $parcel$export(e, n, v, s) {
  Object.defineProperty(e, n, {get: v, set: s, enumerable: true, configurable: true});
}
var $36b2c435d5f32f05$exports = {};

$parcel$export($36b2c435d5f32f05$exports, "useSelect", () => $36b2c435d5f32f05$export$e64b2f635402ca43);







function $36b2c435d5f32f05$export$e64b2f635402ca43(props, state, ref) {
    let { keyboardDelegate: keyboardDelegate , isDisabled: isDisabled  } = props;
    // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
    // When virtualized, the layout object will be passed in as a prop and override this.
    let collator = $9GmRZ$useCollator({
        usage: 'search',
        sensitivity: 'base'
    });
    let delegate = $9GmRZ$useMemo(()=>keyboardDelegate || new $9GmRZ$ListKeyboardDelegate(state.collection, state.disabledKeys, null, collator)
    , [
        keyboardDelegate,
        state.collection,
        state.disabledKeys,
        collator
    ]);
    let { menuTriggerProps: menuTriggerProps , menuProps: menuProps  } = $9GmRZ$useMenuTrigger({
        isDisabled: isDisabled,
        type: 'listbox'
    }, state, ref);
    let onKeyDown = (e)=>{
        switch(e.key){
            case 'ArrowLeft':
                {
                    // prevent scrolling containers
                    e.preventDefault();
                    let key = state.selectedKey != null ? delegate.getKeyAbove(state.selectedKey) : delegate.getFirstKey();
                    if (key) state.setSelectedKey(key);
                    break;
                }
            case 'ArrowRight':
                {
                    // prevent scrolling containers
                    e.preventDefault();
                    let key = state.selectedKey != null ? delegate.getKeyBelow(state.selectedKey) : delegate.getFirstKey();
                    if (key) state.setSelectedKey(key);
                    break;
                }
        }
    };
    let { typeSelectProps: typeSelectProps  } = $9GmRZ$useTypeSelect({
        keyboardDelegate: delegate,
        selectionManager: state.selectionManager,
        onTypeSelect (key) {
            state.setSelectedKey(key);
        }
    });
    let { labelProps: labelProps , fieldProps: fieldProps , descriptionProps: descriptionProps , errorMessageProps: errorMessageProps  } = $9GmRZ$useField({
        ...props,
        labelElementType: 'span'
    });
    typeSelectProps.onKeyDown = typeSelectProps.onKeyDownCapture;
    delete typeSelectProps.onKeyDownCapture;
    let domProps = $9GmRZ$filterDOMProps(props, {
        labelable: true
    });
    let triggerProps = $9GmRZ$mergeProps(typeSelectProps, menuTriggerProps, fieldProps);
    let valueId = $9GmRZ$useId();
    return {
        labelProps: {
            ...labelProps,
            onClick: ()=>{
                if (!props.isDisabled) {
                    ref.current.focus();
                    // Show the focus ring so the user knows where focus went
                    $9GmRZ$setInteractionModality('keyboard');
                }
            }
        },
        triggerProps: $9GmRZ$mergeProps(domProps, {
            ...triggerProps,
            onKeyDown: $9GmRZ$chain(triggerProps.onKeyDown, onKeyDown, props.onKeyDown),
            onKeyUp: props.onKeyUp,
            'aria-labelledby': [
                triggerProps['aria-labelledby'],
                triggerProps['aria-label'] && !triggerProps['aria-labelledby'] ? triggerProps.id : null,
                valueId
            ].filter(Boolean).join(' '),
            onFocus (e) {
                if (state.isFocused) return;
                if (props.onFocus) props.onFocus(e);
                state.setFocused(true);
            },
            onBlur (e) {
                if (state.isOpen) return;
                if (props.onBlur) props.onBlur(e);
                state.setFocused(false);
            }
        }),
        valueProps: {
            id: valueId
        },
        menuProps: {
            ...menuProps,
            autoFocus: state.focusStrategy || true,
            shouldSelectOnPressUp: true,
            shouldFocusOnHover: true,
            disallowEmptySelection: true,
            onBlur: (e)=>{
                if (e.currentTarget.contains(e.relatedTarget)) return;
                if (props.onBlur) props.onBlur(e);
                state.setFocused(false);
            },
            'aria-labelledby': [
                fieldProps['aria-labelledby'],
                triggerProps['aria-label'] && !fieldProps['aria-labelledby'] ? triggerProps.id : null
            ].filter(Boolean).join(' ')
        },
        descriptionProps: descriptionProps,
        errorMessageProps: errorMessageProps
    };
}


var $b6311686a239550d$exports = {};

$parcel$export($b6311686a239550d$exports, "useHiddenSelect", () => $b6311686a239550d$export$f809e80f58e251d1);
$parcel$export($b6311686a239550d$exports, "HiddenSelect", () => $b6311686a239550d$export$cbd84cdb2e668835);



function $b6311686a239550d$export$f809e80f58e251d1(props, state, triggerRef) {
    let { autoComplete: autoComplete , name: name , isDisabled: isDisabled  } = props;
    let modality = $9GmRZ$useInteractionModality();
    let { visuallyHiddenProps: visuallyHiddenProps  } = $9GmRZ$useVisuallyHidden();
    var _selectedKey;
    // In Safari, the <select> cannot have `display: none` or `hidden` for autofill to work.
    // In Firefox, there must be a <label> to identify the <select> whereas other browsers
    // seem to identify it just by surrounding text.
    // The solution is to use <VisuallyHidden> to hide the elements, which clips the elements to a
    // 1px rectangle. In addition, we hide from screen readers with aria-hidden, and make the <select>
    // non tabbable with tabIndex={-1}.
    //
    // In mobile browsers, there are next/previous buttons above the software keyboard for navigating
    // between fields in a form. These only support native form inputs that are tabbable. In order to
    // support those, an additional hidden input is used to marshall focus to the button. It is tabbable
    // except when the button is focused, so that shift tab works properly to go to the actual previous
    // input in the form. Using the <select> for this also works, but Safari on iOS briefly flashes
    // the native menu on focus, so this isn't ideal. A font-size of 16px or greater is required to
    // prevent Safari from zooming in on the input when it is focused.
    //
    // If the current interaction modality is null, then the user hasn't interacted with the page yet.
    // In this case, we set the tabIndex to -1 on the input element so that automated accessibility
    // checkers don't throw false-positives about focusable elements inside an aria-hidden parent.
    return {
        containerProps: {
            ...visuallyHiddenProps,
            'aria-hidden': true
        },
        inputProps: {
            type: 'text',
            tabIndex: modality == null || state.isFocused || state.isOpen ? -1 : 0,
            style: {
                fontSize: 16
            },
            onFocus: ()=>triggerRef.current.focus()
            ,
            disabled: isDisabled
        },
        selectProps: {
            tabIndex: -1,
            autoComplete: autoComplete,
            disabled: isDisabled,
            name: name,
            size: state.collection.size,
            value: (_selectedKey = state.selectedKey) !== null && _selectedKey !== void 0 ? _selectedKey : '',
            onChange: (e)=>state.setSelectedKey(e.target.value)
        }
    };
}
function $b6311686a239550d$export$cbd84cdb2e668835(props) {
    let { state: state , triggerRef: triggerRef , label: label , name: name , isDisabled: isDisabled  } = props;
    let { containerProps: containerProps , inputProps: inputProps , selectProps: selectProps  } = $b6311686a239550d$export$f809e80f58e251d1(props, state, triggerRef);
    // If used in a <form>, use a hidden input so the value can be submitted to a server.
    // If the collection isn't too big, use a hidden <select> element for this so that browser
    // autofill will work. Otherwise, use an <input type="hidden">.
    if (state.collection.size <= 300) return(/*#__PURE__*/ $9GmRZ$react.createElement("div", containerProps, /*#__PURE__*/ $9GmRZ$react.createElement("input", inputProps), /*#__PURE__*/ $9GmRZ$react.createElement("label", null, label, /*#__PURE__*/ $9GmRZ$react.createElement("select", selectProps, /*#__PURE__*/ $9GmRZ$react.createElement("option", null), [
        ...state.collection.getKeys()
    ].map((key)=>{
        let item = state.collection.getItem(key);
        if (item.type === 'item') return(/*#__PURE__*/ $9GmRZ$react.createElement("option", {
            key: item.key,
            value: item.key
        }, item.textValue));
    })))));
    else if (name) return(/*#__PURE__*/ $9GmRZ$react.createElement("input", {
        type: "hidden",
        autoComplete: selectProps.autoComplete,
        name: name,
        disabled: isDisabled,
        value: state.selectedKey
    }));
    return null;
}




export {$36b2c435d5f32f05$export$e64b2f635402ca43 as useSelect, $b6311686a239550d$export$f809e80f58e251d1 as useHiddenSelect, $b6311686a239550d$export$cbd84cdb2e668835 as HiddenSelect};
//# sourceMappingURL=module.js.map
